import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAxios } from "../../utils/hooks/useAxios";
import { useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { handleFormErrors } from "../../utils/helpers";
import dayjs from "dayjs";
import "dayjs/locale/en";
import { useSearchParams } from "react-router-dom";
import { usePortDependOnSystem } from "../../utils/hooks/usePortDependOnSystem";
import { filterObject } from "../../utils/helpers";
import { PickKeyPairs } from "@/utils/helpers";
import { produce } from "immer";
import { REFRESH_INTERVAL } from "@/utils/constants";
import { useSystem } from "@/utils/hooks/useLocation";
import { MAKKA_DEPARTURE_API_BASE } from "@/services/Departure/departures";
import { SYSTEMS } from "@/utils/permissions";
// ******* Constants *******
export const busCardKeys = [
  "bus_id",
  "capacity",
  "departure_date",
  "departure_time",
  "id",
];

// ******* Queries *******

const busCardQueryKeys = [...busCardKeys, "status", "port", "direction"];

export const useGetArrivalsQuery = (params = {}) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  const system = currentSystem === SYSTEMS.MAKKAH.code ? "makkah" : "madina";
  const query = filterObject(params);
  const url = `${system}/arrival-campaigns`;

  return useQuery({
    queryKey: ["arrivals-makkah", query],
    queryFn: () => axiosProtected.get(url, {
      params: query,
    }),
  });
};

export const useGetOneArrivalQuery = ({ id, port = "JED" }) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  const system = currentSystem === SYSTEMS.MAKKAH.code ? "makkah" : "madina";
  return useQuery({
    queryKey: ["one-arrival", id, port],
    queryFn: () => axiosProtected.get(`${system}/arrival-campaigns/${id}`),
    enabled: !!id && !!port,
  });
};
export const useGetOneDepartureQuery = ({ campaignNumber }) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  return useQuery({
    queryKey: ["one-departure", campaignNumber],
    queryFn: () => axiosProtected.get(`${currentSystem}/departure-campaigns/${campaignNumber}`),
    enabled: !!campaignNumber,
  });
};

export const useGetOneArrivalSegmentQuery = ({ id, enabled = true }) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  const system = currentSystem === SYSTEMS.MAKKAH.code ? "makkah" : "madina";
  return useQuery({
    queryKey: ["one-arrival-segment", id],
    queryFn: () => axiosProtected.get(`${system}/arrival-segments/${id}`),
    enabled: enabled && !!id,
  });
};

// ******* Mutations *******

export const useCreateDepartureMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentSystem } = useSystem();
  return useMutation({
    mutationFn: (payload) => axiosProtected.post(`${currentSystem}/departure-campaigns`, payload),
    onSuccess: () => {
      toast.success(t("DEPARTURE_CREATED_SUCCESSFULLY"));
      queryClient.invalidateQueries({ queryKey: ["one-departure"] });
    }
  });
};
export const useUpdateDepartureMutation = ({ listType = "campaigns" } = {}) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const base =
    listType === "groups"
      ? MAKKA_DEPARTURE_API_BASE.groups
      : MAKKA_DEPARTURE_API_BASE.campaigns;

  return useMutation({
    mutationFn: ({ id, ...payload }) => axiosProtected.post(`${base}/${id}`, payload),
    onSuccess: () => {
      toast.success(t("DEPARTURE_CREATED_SUCCESSFULLY"));
      queryClient.invalidateQueries({ queryKey: ["one-departure"] });
      queryClient.invalidateQueries({
        queryKey: [listType === "groups" ? "departure-group" : "campaign-and-group-departure"],
      });
    }
  });
};
export const useDeleteDepartureMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentSystem } = useSystem();
  return useMutation({
    mutationFn: ({ id }) => axiosProtected.delete(`${currentSystem}/departure-campaigns/delete/${id}`),
    onSuccess: () => {
      toast.success(t("DELETE_DEPARTURE_SUCCESSFULLY"));
      queryClient.invalidateQueries({ queryKey: ["one-departure"] });
    }
  });
};

export const useUpdateArrivalMutation = ({ setError }) => {
  const { axiosProtected } = useAxios();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ flight_date, phoneCountryCode, ...payload }) => {
      return axiosProtected.post(`makkah/arrivals/update`, {
        ...payload,
        flight_date: flight_date
          ? dayjs(flight_date, "YYYY-MM-DD HH:mm").format("YYYY-MM-DD HH:mm:ss")
          : null,
        mobile_number: `${phoneCountryCode}${payload.mobile_number}`,
      });
    },
    onSuccess: ($_, variables) => {
      toast.success(t("ARRIVAL_UPDATED_SUCCESSFULLY"));
      queryClient.invalidateQueries({
        queryKey: ["one-arrival", variables.id, variables.port],
      });
      navigate("/app/makkah/arrival-campaign");
    },
    onError: (err) => {
      console.error("Arrival update error:", err);
      if (setError) {
        handleFormErrors(err, setError, t, "ARRIVAL_UPDATE_FAILED");
      }
      toast.error(err?.response?.data?.message || t("ARRIVAL_UPDATE_FAILED"));
    },
  });
};

// Dedicated mutation for updating a single arrival detail (flight) without navigation
export const useUpdateArrivalDetailMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { id } = useParams();

  return useMutation({
    mutationFn: ({ flight_date, phoneCountryCode, ...payload }) => {
      return axiosProtected.post(`makkah/arrivals/update`, {
        ...payload,
        flight_date: flight_date
          ? dayjs(flight_date, "YYYY-MM-DD HH:mm").format("YYYY-MM-DD HH:mm:ss")
          : null,
        mobile_number: `${payload.mobile_number}`,
      });
    },
    onSuccess: (_data, variables) => {
      toast.success(t("ARRIVAL_UPDATED_SUCCESSFULLY"));
      // Invalidate the specific arrival details
      queryClient.invalidateQueries({
        queryKey: ["one-arrival", id, String(variables.port)],
      });
    },
    onError: (err) => { },
  });
};

export const useImportArrivalMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();

      return axiosProtected.post("makkah/import-arrivals", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    },
    onSuccess: () => {
      toast.success(t("CAMPAIGN_IMPORTED_SUCCESSFULLY"));
      queryClient.invalidateQueries({ queryKey: ["arrivals-makkah"] });
    },
    onError: (err) => {
      console.error("Campaign import error:", err);
      toast.error(err?.response?.data?.message || t("CAMPAIGN_IMPORT_FAILED"));
    },
  });
};

export const useImportDepartureMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentSystem } = useSystem();
  return useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();

      return axiosProtected.post(`${currentSystem}/departure-campaigns/import`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    },
    onSuccess: () => {
      toast.success(t("CAMPAIGN_IMPORTED_SUCCESSFULLY"));
      queryClient.invalidateQueries({ queryKey: ["arrivals-makkah"] });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || t("CAMPAIGN_IMPORT_FAILED"));
    },
  });
};

export const useGetAirportArrivalsQuery = (params = {}) => {
  const { axiosProtected } = useAxios();
  const { port } = useParams();
  const [searchParams] = useSearchParams();
  const source = searchParams.get("source") || "delegation";
  const { date: dateQuery, ...restParams } = params;

  const nusukUrl =
    dateQuery === "today"
      ? "makkah/nusk-arrival-flights/today"
      : `makkah/nusk-arrival-flights`;
  const betweenCitiesUrl =
    dateQuery === "today" ? "makkah/tafweej/today" : "makkah/tafweej";
  const campaignDelegationUrl = `makkah/airport-arrivals`;

  const URL = {
    delegation: campaignDelegationUrl,
    campaign: campaignDelegationUrl,
    between_cities: betweenCitiesUrl,
    nusuk: nusukUrl,
  };

  const undefinedUrl = {
    data: {
      data: [],
    },
  };

  const { filters, ...restParamsWithoutFilters } = restParams;


  const direaction_id =
    port === "JED" && source === "nusuk"
      ? 1
      : port === "MED" && source === "nusuk"
        ? 2
        : null;
  
  return useQuery({
    queryKey: ["airport-arrivals", params],
    queryFn: () =>
      URL[source]
        ? axiosProtected.get(URL[source], {
          params: filterObject({
            direaction_id,
            ...restParamsWithoutFilters,
            ...filters,
          }),
        })
        : undefinedUrl,
    refetchInterval: REFRESH_INTERVAL,
    refetchIntervalInBackground: true,
  });
};
export const useGetCheckinArrivalsQuery = (params = {}) => {
  const { axiosProtected } = useAxios();
  const { port } = useParams();
  const [searchParams] = useSearchParams();
  const source = searchParams.get("source") || "delegation";
  const { date: dateQuery, ...restParams } = params;

  const nusukUrl =
    dateQuery === "today"
      ? "makkah/nusk-arrival-flights/today"
      : `makkah/nusk-arrival-flights`;
  const betweenCitiesUrl =
    dateQuery === "today" ? "makkah/tafweej/today" : "makkah/tafweej";
  const campaignDelegationUrl = `makkah/checkin-arrivals`;

  const URL = {
    delegation: campaignDelegationUrl,
    campaign: campaignDelegationUrl,
    between_cities: betweenCitiesUrl,
    nusuk: nusukUrl,
  };

  const undefinedUrl = {
    data: {
      data: [],
    },
  };

  const { filters, ...restParamsWithoutFilters } = restParams;


  const direaction_id =
    port === "JED" && source === "nusuk"
      ? 1
      : port === "MED" && source === "nusuk"
        ? 2
        : null;
  
  return useQuery({
    queryKey: ["airport-arrivals", params],
    queryFn: () =>
      URL[source]
        ? axiosProtected.get(URL[source], {
          params: filterObject({
            direaction_id,
            ...restParamsWithoutFilters,
            ...filters,
          }),
        })
        : undefinedUrl,
    refetchInterval: REFRESH_INTERVAL,
    refetchIntervalInBackground: true,
  });
};
export const useGetServiceCenterArrivalsQuery = (params = {}) => {
  const { axiosProtected } = useAxios();
  const { port } = useParams();
  const [searchParams] = useSearchParams();
  const source = searchParams.get("source") || "delegation";
  const { date: dateQuery, ...restParams } = params;

  const nusukUrl =
    dateQuery === "today"
      ? "makkah/nusk-arrival-flights/today"
      : `makkah/nusk-arrival-flights`;
  const betweenCitiesUrl =
    dateQuery === "today" ? "makkah/tafweej/today" : "makkah/tafweej";
  const campaignDelegationUrl = `makkah/service_center-arrivals`;

  const URL = {
    delegation: campaignDelegationUrl,
    campaign: campaignDelegationUrl,
    between_cities: betweenCitiesUrl,
    nusuk: nusukUrl,
  };

  const undefinedUrl = {
    data: {
      data: [],
    },
  };

  const { filters, ...restParamsWithoutFilters } = restParams;


  const direaction_id =
    port === "JED" && source === "nusuk"
      ? 1
      : port === "MED" && source === "nusuk"
        ? 2
        : null;

  return useQuery({
    queryKey: ["airport-arrivals", params],
    queryFn: () =>
      URL[source]
        ? axiosProtected.get(URL[source], {
          params: filterObject({
            direaction_id,
            ...restParamsWithoutFilters,
            ...filters,
          }),
        })
        : undefinedUrl,
    refetchInterval: REFRESH_INTERVAL,
    refetchIntervalInBackground: true,
  });
};

export const useMarkAsArrivedMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ port, ...payload }) => {
      return axiosProtected.post("makkah/arrive-airport", {
        ...payload,
        port: port || "JED",
      });
    },
    onSuccess: () => {
      toast.success(t("MARKED_ARRIVED_SUCCESSFULLY"));
      queryClient.invalidateQueries({ queryKey: ["airport-arrivals"] });
    },
    onError: (err) => {
      console.error("Mark as arrived error:", err);
      toast.error(err?.response?.data?.message || t("ARRIVAL_MARK_FAILED"));
    },
  });
};

export const useCreateBusCardMutation = (setFormError, params) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { port } = useParams();

  return useMutation({
    mutationFn: ({ arrivalIndex, ...payload }) => {
      return axiosProtected.post("makkah/campaign-bus-cards", { ...payload, port });
    },
    onSuccess: (data, variables) => {
      toast.success(t("BUS_CARD_CREATED_SUCCESSFULLY"));

      queryClient.setQueryData(["airport-arrivals", params], (old) => {
        return produce(old, (draft) => {
          draft.data.data[variables.arrivalIndex].bus_cards =
            data.data.data.bus_cards.map((busCard) =>
              PickKeyPairs(busCard, busCardQueryKeys)
            );
        });
      });
    },
    onError: (err) => {
      handleFormErrors(err, setFormError, t, "BUS_CARD_CREATION_FAILED");
    },
  });
};

export const useMarkBusAsArrivedMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ busCardId }) => {
      return axiosProtected.get(
        `makkah/campaign-bus-cards/${busCardId}/arrive-hotels`
      );
    },
    onSuccess: () => {
      toast.success(t("BUS_MARKED_ARRIVED_SUCCESSFULLY"));
      queryClient.invalidateQueries({ queryKey: ["airport-arrivals"] });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || t("FAILED_MARK_BUS_ARRIVED"));
    },
  });
};

export const useCancelBusArrivalMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ busCardId }) => {
      return axiosProtected.post(
        `makkah/campaign-bus-cards/${busCardId}/cancel-arrival`
      );
    },
    onSuccess: () => {
      toast.success(t("BUS_ARRIVAL_CANCELLED_SUCCESSFULLY"));
      queryClient.invalidateQueries({ queryKey: ["airport-arrivals"] });
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.message || t("FAILED_CANCEL_BUS_ARRIVAL")
      );
    },
  });
};

export const useCancelFlightMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const port = usePortDependOnSystem();

  return useMutation({
    mutationFn: ({ campaign_id, ...payload }) => {
      return axiosProtected.post(
        `makkah/airport-arrivals/cancel?port=${port}&campaign_id=${campaign_id}`,
        {
          ...payload,
        }
      );
    },
    onSuccess: () => {
      toast.success(t("CANCEL_FLIGHT_SUCCESSFULLY"));
      queryClient.invalidateQueries({ queryKey: ["airport-arrivals"] });
    },
    onError: (err) => {
      console.error("Cancel flight error:", err);
      toast.error(err?.response?.data?.message || t("CANCEL_FLIGHT_FAILED"));
    },
  });
};

export const useRestoreFlightMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const port = usePortDependOnSystem();

  return useMutation({
    mutationFn: ({ campaign_id, ...payload }) => {
      return axiosProtected.post(
        `makkah/airport-arrivals/mark-not-arrived?port=${port}&campaign_id=${campaign_id}`,
        {
          ...payload,
        }
      );
    },
    onSuccess: () => {
      toast.success(t("RESTORE_FLIGHT_SUCCESSFULLY"));
      queryClient.invalidateQueries({ queryKey: ["airport-arrivals"] });
    },
    onError: (err) => {
      console.error("Restore flight error:", err);
      toast.error(err?.response?.data?.message || t("RESTORE_FLIGHT_FAILED"));
    },
  });
};

export const useDeleteBusCardMutation = (params) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ busCardId }) => {
      return axiosProtected.get(
        `makkah/campaign-bus-cards/delete/${busCardId}`
      );
    },
    onSuccess: ($, variables) => {
      toast.success(t("BUS_CARD_DELETED_SUCCESSFULLY"));
      queryClient.setQueryData(["airport-arrivals", params], (old) => {
        return produce(old, (draft) => {
          draft.data.data[variables.arrivalIndex].bus_cards = draft.data.data[
            variables.arrivalIndex
          ].bus_cards.filter((busCard) => busCard.id !== variables.busCardId);
        });
      });
    },
    onError: (err) => {
      console.error("Delete bus card error:", err);
      toast.error(err?.response?.data?.message || t("BUS_CARD_DELETE_FAILED"));
    },
  });
};

export const useDepartAirportMutation = (params) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ busCardId }) => {
      return axiosProtected.get(
        `makkah/campaign-bus-cards/${busCardId}/depart-airport`
      );
    },
    onSuccess: (data, variables) => {
      toast.success(t("BUS_DEPARTED_SUCCESSFULLY"));
      const nextState = (arrivals) =>
        produce(arrivals, (draft) => {
          const { arrivalIndex, busCardIndex } = variables;
          draft.data.data[arrivalIndex].bus_cards[busCardIndex] = PickKeyPairs(
            data.data.data,
            busCardQueryKeys
          );
        });
      queryClient.setQueryData(["airport-arrivals", params], nextState);
    },
    onError: (err) => {
      console.error("Depart airport error:", err);
      toast.error(err?.response?.data?.message || t("BUS_DEPART_FAILED"));
    },
  });
};

export const useMarkBusNotArrivedHotelsMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ busCardId }) => {
      return axiosProtected.get(
        `makkah/campaign-bus-cards/${busCardId}/mark-not-arrived-hotels`
      );
    },
    onSuccess: (data, variables) => {
      toast.success(
        t("CANCEL_ARRIVAL_SUCCESS") || t("ARRIVAL_CANCELLED_SUCCESSFULLY")
      );

      // >>> update client data <<<
      queryClient.invalidateQueries({ queryKey: ["airport-arrivals"] });
    },
    onError: (err) => {
      console.error("Mark bus not arrived hotels error:", err);
      toast.error(
        err?.response?.data?.message || t("FAILED_CANCEL_BUS_ARRIVAL")
      );
    },
  });
};

export const useMarkBusNotDepartedMutation = (params) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ busCardId }) => {
      return axiosProtected.get(
        `makkah/campaign-bus-cards/${busCardId}/mark-not-departed`
      );
    },
    onSuccess: (data, variables) => {
      toast.success(t("CANCEL_DEPARTURE_SUCCESS") || "تم إلغاء التحرك بنجاح");
      queryClient.setQueryData(["airport-arrivals", params], (old) => {
        return produce(old, (draft) => {
          draft.data.data[variables.arrivalIndex].bus_cards[
            variables.busCardIndex
          ] = PickKeyPairs(data.data.data, busCardQueryKeys);
        });
      });
    },
    onError: (err) => {
      console.error("Mark bus not departed error:", err);
      toast.error(
        err?.response?.data?.message ||
        t("CANCEL_DEPARTURE_FAILED") ||
        "فشل إلغاء التحرك"
      );
    },
  });
};

// Helper function to generate day names from flight_date
const generateDayNames = (flightDate) => {
  if (!flightDate) {
    return { day_name_ar: null, day_name_en: null };
  }

  const date = dayjs(flightDate);
  if (!date.isValid()) {
    return { day_name_ar: null, day_name_en: null };
  }

  const dayOfWeek = date.day();

  const arabicDays = [
    "الأحد",
    "الإثنين",
    "الثلاثاء",
    "الأربعاء",
    "الخميس",
    "الجمعة",
    "السبت",
  ];

  const englishDays = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  return {
    day_name_ar: arabicDays[dayOfWeek],
    day_name_en: englishDays[dayOfWeek],
  };
};

// Helper function to map form data to API payload
const mapSegmentFormToPayload = (formData, arrivalCampaignId) => {
  const { day_name_ar, day_name_en } = generateDayNames(formData.flight_date);

  return {
    arrival_campaign_id: arrivalCampaignId,
    flight_date: formData.flight_date,
    flight_number: formData.flight_number || null,
    airline_id: formData.airline_id || null,
    contract_id: formData.contract_id || null,
    entry_port_id: formData.entry_port_id || null,
    direction_id: formData.direction_id || null,
    day_name_ar: day_name_ar,
    day_name_en: day_name_en,
    employee_number: formData.employee_number || null,
    capacity: formData.capacity || null,
    buses_count: formData.buses_count || null,
    coming_from_id: formData.coming_from_id || null,
    transport_method_id: formData.transport_method_id || null,
    data_source: "manual",
    status: "scheduled",
    real_datetime: formData.real_datetime,
  };
};

// ******* Segment Mutations *******
export const useCreateArrivalSegmentMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { id, port } = useParams();
  const { currentSystem } = useSystem();
  const system = currentSystem === SYSTEMS.MAKKAH.code ? "makkah" : "madina";
  return useMutation({
    mutationFn: (payload) => {
      return axiosProtected.post(`${system}/arrival-segments`, {
        arrival_campaign_id: parseInt(id),
        ...payload,
      });
    },
    onSuccess: (data) => {
      toast.success(
        t("SEGMENT_CREATED_SUCCESSFULLY") || "تم إنشاء القطاع بنجاح"
      );
      queryClient.setQueryData(["one-arrival", id, port], (old) => {
        return produce(old, (draft) => {
          draft.data.data.segments.push(data.data.data);
        });
      });
    },
    onError: (err) => {
      console.error("Segment creation error:", err);
      toast.error(
        err?.response?.data?.message ||
        t("SEGMENT_CREATION_FAILED") ||
        "فشل إنشاء القطاع"
      );
    },
  });
};

export const useUpdateArrivalSegmentMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { id, port } = useParams();
  const { currentSystem } = useSystem();
  const system = currentSystem === SYSTEMS.MAKKAH.code ? "makkah" : "madina";
  return useMutation({
    mutationFn: ({ segmentId, ...formData }) => {
      const payload = mapSegmentFormToPayload(formData, parseInt(id));
      return axiosProtected.post(
        `${system}/arrival-segments/update/${segmentId}`,
        payload
      );
    },
    onSuccess: (data, variables) => {
      toast.success(
        t("SEGMENT_UPDATED_SUCCESSFULLY") || "تم تحديث القطاع بنجاح"
      );
      queryClient.setQueryData(["one-arrival", id, port], (old) => {
        return produce(old, (draft) => {
          draft.data.data.segments = draft.data.data.segments.map((segment) =>
            segment.id === variables.segmentId ? data.data.data : segment
          );
        });
      });
    },
    onError: (err) => {
      console.error("Segment update error:", err);
      toast.error(
        err?.response?.data?.message ||
        t("SEGMENT_UPDATE_FAILED") ||
        "فشل تحديث القطاع"
      );
    },
  });
};

export const useDeleteArrivalSegmentMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { id, port } = useParams();
  const { currentSystem } = useSystem();
  const system = currentSystem === SYSTEMS.MAKKAH.code ? "makkah" : "madina";
  return useMutation({
    mutationFn: ({ segmentId }) => {
      return axiosProtected.delete(
        `${system}/arrival-segments/delete/${segmentId}`
      );
    },
    onSuccess: ($, variables) => {
      toast.success(t("SEGMENT_DELETED_SUCCESSFULLY") || "تم حذف القطاع بنجاح");
      queryClient.setQueryData(["one-arrival", id, port], (old) => {
        return produce(old, (draft) => {
          draft.data.data.segments = draft.data.data.segments.filter(
            (segment) => segment.id !== variables.segmentId
          );
        });
      });
    },
    onError: (err) => {
      console.error("Segment delete error:", err);
      toast.error(
        err?.response?.data?.message ||
        t("SEGMENT_DELETE_FAILED") ||
        "فشل حذف القطاع"
      );
    },
  });
};
